import { rgba } from 'polished';
import React, { useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	Button,
	FlexGap,
	GearIcon,
	IconButton,
	JunkIcon,
	Popover,
	ReorderIcon,
	tokens,
} from '@treams/treams-ui';

import { pickEvaluationSectionItem } from '../utilities/pickEvaluationSectionItem';
import EditModal from './EditModal';
import Competency from './items/Competency';
import DevelopmentPlan from './items/DevelopmentPlan';
import FinalScore from './items/FinalScore';
import OpenQuestion from './items/OpenQuestion';
import Paragraph from './items/Paragraph';
import ReviewGoals from './items/ReviewGoals';
import Sentiment from './items/Sentiment';
import SetGoals from './items/SetGoals';
import Signature from './items/Signature';
import Stars from './items/Stars';
import Title from './items/Title';

const Card = styled.div<{ dragging: boolean }>`
	background: ${tokens.surface.secondary};
	padding: 20px;
	min-height: 60px;
	border-bottom: 1px solid ${tokens.border.subtle};

	${(props) =>
		props.dragging &&
		`
	border-radius: 5px;
	box-shadow: 0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.1)},
			    0px 4px 8px ${rgba(tokens.text.secondary, 0.25)},
			    0px 32px 64px ${rgba(tokens.text.secondary, 0.25)};
	border-bottom-color: transparent;
	`}
`;

const Toolbar = styled(FlexGap)`
	flex-shrink: 0;
`;

function ItemCardLayout(props: {
	center: boolean;
	toolbar: React.ReactNode;
	children: React.ReactNode;
}) {
	return props.center ? (
		<Flex alignItems="center">
			<Box flexGrow={1} flexShrink={1} style={{ pointerEvents: 'none' }}>
				{props.children}
			</Box>
			<Toolbar alignItems="center" ml={3} gap={tokens.gap.element}>
				{props.toolbar}
			</Toolbar>
		</Flex>
	) : (
		<>
			<Toolbar alignItems="center" style={{ float: 'right' }} ml={3} gap={tokens.gap.element}>
				{props.toolbar}
			</Toolbar>
			<Box flexGrow={1} flexShrink={1} style={{ pointerEvents: 'none' }}>
				{props.children}
			</Box>
		</>
	);
}

export default function ItemCard(props: {
	item: graphql.EvaluationSectionItem;
	dragging: boolean;
	provided?: DraggableProvided;
	onUpdate: (id: string, item?: graphql.EvaluationSectionItem) => void;
	onDelete: (id: string) => void;
}) {
	const { t } = useTranslation();
	const [edit, setEdit] = useState(false);

	const title = pickEvaluationSectionItem(props.item, {
		competency: t('common.settings.evaluationForms.competency.title'),
		openQuestion: t('common.settings.evaluationForms.openQuestion.title'),
		sentiment: t('common.settings.evaluationForms.sentiment.title'),
		signature: t('common.settings.evaluationForms.signature.title'),
		stars: t('common.settings.evaluationForms.stars.title'),
		title: t('common.settings.evaluationForms.title.title'),
		paragraph: t('common.settings.evaluationForms.paragraph.title'),
		finalScore: t('common.settings.evaluationForms.finalScore.title'),
		reviewGoals: t('common.settings.evaluationForms.reviewGoals.title'),
		developmentPlan: t('common.settings.evaluationForms.developmentPlan.title'),
		setGoals: t('common.settings.evaluationForms.setGoals.title'),
	});

	const closeTooltip = () => {
		if (!localStorage.getItem('selectingMedia')) {
			setEdit(false);
		}
	};

	return (
		<Card
			dragging={props.dragging}
			{...(!edit && props.provided?.draggableProps)}
			ref={props.provided?.innerRef}
		>
			<ItemCardLayout
				center={!!props.item.competency || !!props.item.title}
				toolbar={
					<>
						<Popover.Target
							onDismiss={closeTooltip}
							visible={edit}
							contents={<EditModal item={props.item} onUpdate={props.onUpdate} />}
						>
							<Button
								type="plain"
								rightIcon={<GearIcon />}
								onClick={() => setEdit(true)}
								title={title}
							/>
						</Popover.Target>
						<IconButton
							icon={<JunkIcon />}
							type="destructive"
							onClick={() => props.onDelete(props.item.id)}
						/>
						<IconButton
							icon={<ReorderIcon />}
							type="ghost"
							{...(!edit && props.provided?.dragHandleProps)}
						/>
					</>
				}
			>
				{props.item.competency && <Competency name="item" item={props.item.competency} />}
				{props.item.openQuestion && <OpenQuestion name="item" item={props.item.openQuestion} />}
				{props.item.paragraph && <Paragraph item={props.item.paragraph} />}
				{props.item.sentiment && <Sentiment name="item" item={props.item.sentiment} />}
				{props.item.signature && <Signature name="item" item={props.item.signature} />}
				{props.item.stars && <Stars name="item" item={props.item.stars} />}
				{props.item.title && <Title item={props.item.title} />}
				{props.item.finalScore && <FinalScore name="item" item={props.item.finalScore} />}
				{props.item.reviewGoals && <ReviewGoals />}
				{props.item.developmentPlan && (
					<DevelopmentPlan name="item" item={props.item.developmentPlan} />
				)}
				{props.item.setGoals && <SetGoals name="item" item={props.item.setGoals} />}
			</ItemCardLayout>
		</Card>
	);
}
